import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    ValidationMessage,
    ValidationMessageType,
    SettingState,
    TibboDataType,
    SettingOption,
    Define,
} from '@appblocks/codegeneration/types';
import * as micropython from './micropython';
import * as tios from './tios';
import * as zephyr from './zephyr';
import * as simulator from './simulator';

export default class MQTT extends BaseFeature {
    constructor() {
        super();
        this.name = 'MQTT';
        this.id = 'mqtt';
        this.icon = 'AiOutlineCloudUpload';
        this.fileLinks = [
            // 'mqtt\\mqtt.tbs',
            // 'mqtt\\mqtt.tbh',
        ];
        this.requirements = ['network'];

        const settings = [
            {
                id: 'GENERAL',
                name: 'General',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'CERTIFICATE',
                name: 'Certificate Upload',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'ENABLED',
                name: 'MQTT Enabled',
                settingType: SettingType.OPTION,
                required: true,
                group: 'GENERAL',
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'MQTT_APP_CLIENT_ID',
                name: 'Client ID',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'MQTTI',
                    NAME: 'MQTT Client ID',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 50,
                    DEFAULT: 'Client_ID_1',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                group: 'GENERAL',
                helpText: 'Each client connecting to MQTT server must have a **unique** ID.',
                default: 'Client_ID_1',
            },
            {
                id: 'MQTT_NAME',
                name: 'Username',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'MQTTU',
                    NAME: 'MQTT Username',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 50,
                    DEFAULT: 'MQTT_Name_1',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                group: 'GENERAL',
                default: 'MQTT_Name_1',
            },
            {
                id: 'MQTT_PASSWORD',
                name: 'Password',
                settingType: SettingType.PASSWORD,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'MQTPW',
                    NAME: 'MQTT Password',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 50,
                    DEFAULT: 'Password',
                },
                UIControl: {
                    CONTROL: 'PASSWORD',
                },
                required: true,
                group: 'GENERAL',
                default: 'Password',
            },
            {
                id: 'MQTT_SERVER_HOST',
                name: 'Server Host Address/IP',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'MQTTH',
                    NAME: 'MQTT Server Host Address/IP',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 50,
                    DEFAULT: '1.0.0.1',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                required: true,
                group: 'GENERAL',
                default: '1.0.0.1',
            },
            {
                id: 'MQTT_DESTINATION_PORT',
                name: 'Server Port',
                settingType: SettingType.NUMBER,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'MQTSP',
                    NAME: 'MQTT Server Port',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 5,
                    DEFAULT: '1883',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                max: 65535,
                required: true,
                group: 'GENERAL',
                default: '1883',
            },
            {
                id: 'MQTT_TLS',
                name: 'TLS',
                settingType: SettingType.OPTION,
                required: true,
                group: 'GENERAL',
                helpText: 'Enable TLS encryption. Supports sending of packets of up to 1000B',
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'MQTT_DEBUG_PRINT',
                name: 'Debug Print',
                settingType: SettingType.OPTION,
                required: true,
                group: 'GENERAL',
                helpText: 'Debug print messages',
                default: '1',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'CERTIFICATEFILE',
                name: 'Certificate to Upload',
                settingType: SettingType.FILE,
                required: true,
                group: 'CERTIFICATE',
                default: '',
                max: 102400,
            },
            {
                id: 'TOPICS',
                name: 'Topics',
                settingType: SettingType.ARRAY,
                required: true,
                group: 'GENERAL',
                refId: 'mqtt_topic',
                default: new Array<BaseFeature>(0),
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getDefines(): Array<Define> {
        const defines: Array<Define> = [];
        const MQTT_DEBUG_PRINT = this.getSettingValue('MQTT_DEBUG_PRINT');
        const MQTT_LARGE_PACKETS = this.getSettingValue('MQTT_LARGE_PACKETS');
        if (MQTT_DEBUG_PRINT === '1') {
            defines.push({
                name: 'MQTT_DEBUG_PRINT',
                value: '1',
            });
        }
        return defines;
    }

    getFiles() {
        if (this.project.runtime === 'zephyr') {
            return [
                'src/mqtt_client.h',
                'src/mqtt_client.cpp',
            ];
        }
        const CERTIFICATEFILE = this.getSettingValue('CERTIFICATEFILE');
        const fileArray = [];
        if (CERTIFICATEFILE !== '') {
            fileArray.push(CERTIFICATEFILE.split('\n')[0]);
        }
        return this.files.concat(fileArray);
    }

    getDependencies() {
        const dependencies = [
            'mqtt_lib',
        ];
        return dependencies;
    }

    getState(settingId: string): SettingState {
        if (settingId !== 'ENABLED' && this.getSettingValue('ENABLED').toString() === '0') {
            return SettingState.HIDDEN;
        }
        if (settingId === 'CERTIFICATEFILE' && this.getSettingValue('MQTT_TLS').toString() === '0') {
            return SettingState.HIDDEN;
        }
        switch (settingId) {
            case 'ENABLED':
                return SettingState.ENABLED;
            default:
                return SettingState.ENABLED;
        }
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }

    getDetails(): string {
        return `
## MQTT Client
The device can connect to an MQTT broker to subscribe and poublish to topics.

The device supports both unencrypted and TLS encrypted connections.

The **MQTT Publish** and **On MQTT Notification** blocks can be used with MQTT.

![MQTT blocks](/static/img/blocks_mqtt.png)
        `;
    }
}
